import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Step,
    StepButton,
    Stepper,
    Text,
    VisuallyHidden,
} from '@indeed/ifl-components';
import { ArrowLeft } from '@indeed/ifl-icons';
import { usePageFlow } from '../../lib/pageFlow/usePageFlow';
import { FlowStep } from '../../lib/pageFlow/types';
// Removed useLightApplyForm - navigation now uses Apollo state directly

const LightApplyNavigation: React.FunctionComponent = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { allSteps, goBack, canGoBack, goToStep } = usePageFlow();
    const [isHydrated, setIsHydrated] = useState(false);

    // Prevent hydration mismatch by only showing dynamic content after client hydration
    useEffect(() => {
        setIsHydrated(true);
    }, []);

    // Don't render navigation on post-apply page since the flow is complete
    if (location.pathname.includes('/post-apply')) {
        return null;
    }

    // Filter allSteps to only include routes without trailing numbers > 1, and exclude review/post-apply
    const stepperSteps =
        allSteps?.filter((step) => {
            const primaryRoute = step.routes[0];
            // Exclude review and post-apply routes
            if (primaryRoute.includes('/review') || primaryRoute.includes('/post-apply')) {
                return false;
            }

            const routeParts = primaryRoute.split('/');
            const lastPart = routeParts[routeParts.length - 1];
            const trailingNumber = parseInt(lastPart, 10);

            // Include if no trailing number or trailing number is 1
            return isNaN(trailingNumber) || trailingNumber === 1;
        }) || [];

    // Function to check if a step is completed based on completedSteps state
    const isStepCompleted = (): boolean => {
        // TODO: Step completion now managed by individual pages via Apollo
        // Could query SmartApplyApplicationState to check completion if needed
        return false;
    };

    // Find current step index for stepper display
    const currentStepperIndex = stepperSteps.findIndex((step) => {
        const primaryRoute = step.routes[0];
        if (step.routes.includes(location.pathname)) return true;

        // For multi-step sections like contact-info, check if current path starts with the step's base path
        const stepBasePath = primaryRoute.replace(/\/\d+$/, ''); // Remove trailing number if present
        const currentBasePath = location.pathname.replace(/\/\d+$/, ''); // Remove trailing number if present
        return currentBasePath === stepBasePath;
    });
    const currentStep = currentStepperIndex >= 0 ? currentStepperIndex : 0;

    const handleBack = () => {
        if (canGoBack) {
            void goBack(); // Use flow-aware navigation
        } else {
            // Navigate to homepage - use direct navigation since '/' is not part of any flow
            // Manual search parameter preservation needed for navigate() calls
            void navigate('/' + location.search, { state: { from: location.pathname } });
        }
    };

    const handleSaveAndClose = () => {
        // Save current progress and navigate to home - external navigation is appropriate
        window.location.href = 'https://qa.indeed.net';
    };

    const handleStepClick = (stepIndex: number) => {
        const targetStep = stepperSteps[stepIndex];
        if (targetStep) {
            const primaryRoute = targetStep.routes[0];
            // Check if we're in the contact-info section
            if (primaryRoute.includes('/light-apply/contact-info')) {
                // If clicking on 基本情報 (contact-info), go to step 1 using flow-aware navigation
                void goToStep('/light-apply/contact-info/1');
            } else {
                // For other main flow steps (resume, questions, etc.), use flow-aware navigation
                void goToStep(primaryRoute);
            }
        }
    };

    // Mock job data - in real app this would come from props or context
    const jobData = {
        title: '店舗スタッフ',
        company: '株式会社エービーシー',
        location: '東京都 中央区 日本橋',
    };

    return (
        <div
            style={{
                backgroundColor: '#ffffff',
            }}
        >
            {/* Job Information Header */}
            <Box
                sx={{
                    paddingInlineStart: '8px',
                    paddingInlineEnd: '8px',
                    paddingBlockStart: '16px',
                    paddingBlockEnd: '24px',
                    maxInlineSize: '480px',
                    margin: '0 auto',
                    inlineSize: '100%',
                }}
            >
                <Box
                    sx={{
                        marginBlockEnd: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                        alignItems: 'flex-start',
                        padding: 0,
                    }}
                >
                    <Text
                        variant="contrast"
                        sx={{
                            fontSize: 2,
                            fontWeight: 'bold',
                            marginBlockEnd: 0,
                            padding: 0,
                        }}
                    >
                        {jobData.title}
                    </Text>
                    <Text
                        sx={{
                            fontSize: 1,
                            color: 'foreground.secondary',
                            marginBlockEnd: 0,
                            padding: 0,
                        }}
                    >
                        {jobData.company} - {jobData.location}
                    </Text>
                    <Button
                        variant="text"
                        size="sm"
                        sx={{
                            fontSize: 1,
                            fontWeight: 400,
                            padding: 0,
                            margin: 0,
                            marginBlockStart: '-6px',
                            color: 'action.primary',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        求人詳細を表示する
                    </Button>
                </Box>

                {/* Navigation Controls */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBlockEnd: 6 /* = 24px */,
                    }}
                >
                    <Button
                        size="sm"
                        onClick={handleBack}
                        sx={{
                            minInlineSize: 'auto',
                            opacity: !canGoBack ? 0 : 1,
                            pointerEvents: !canGoBack ? 'none' : 'auto',
                        }}
                        variant="tertiary"
                    >
                        <ArrowLeft />
                    </Button>

                    <Button
                        variant="text"
                        size="sm"
                        onClick={handleSaveAndClose}
                        sx={{
                            fontSize: 1,
                            color: 'action.primary',
                            fontWeight: 'bold',
                        }}
                    >
                        保存して閉じる
                    </Button>
                </Box>

                {/* Official Stepper Component - Hide on review page */}
                {stepperSteps.length > 0 && !location.pathname.includes('/review') && (
                    <Box
                        sx={{
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            overflowX: 'auto' as any,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            WebkitOverflowScrolling: 'touch' as any, // iOS momentum
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            scrollBehavior: 'smooth' as any,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            scrollbarWidth: 'none' as any, // Firefox
                            '&::-webkit-scrollbar': { display: 'none' }, // Chrome/Safari
                        }}
                    >
                        <Stepper activeIndex={isHydrated ? currentStep : 0} variant="nonLinear">
                            {stepperSteps.map((step: FlowStep, index: number) => {
                                const isCompleted = isHydrated ? isStepCompleted() : false;
                                const isActive = isHydrated ? index === currentStep : index === 0;

                                return (
                                    <Step key={step.routes[0]} complete={isCompleted}>
                                        <StepButton onClick={() => handleStepClick(index)}>
                                            {step.label}
                                            <VisuallyHidden>
                                                {isCompleted
                                                    ? 'Complete'
                                                    : isActive
                                                      ? 'Current'
                                                      : 'Available'}
                                            </VisuallyHidden>
                                        </StepButton>
                                    </Step>
                                );
                            })}
                        </Stepper>
                    </Box>
                )}
            </Box>
        </div>
    );
};

export default LightApplyNavigation;
